/**
 * Model Structuring & Layout Skill Content — layout layer for every model.
 *
 * This is a global hygiene skill — pushed, not pulled. It applies to every
 * build and every edit by default. Output and tool skills defer their
 * structure and generic template mechanics to it.
 *
 * It owns: tab layout, workbook organisation, the input/build/output/check
 * zones, named ranges, generic template mechanics, and reading an unfamiliar
 * model's structure. It governs where things sit, not what they compute
 * (Data & Formula skills) or how they look (Model Formatting skills).
 *
 * Skill Type: Model Hygiene
 * Shared Skill: Global
 */

export const STRUCTURE_LAYOUT_SKILL_CONTENT = `## Skill: Model Structuring & Layout

### Purpose
Lay a model out so it is navigable and consistent - inputs segregated and on top, the build flowing to a clear output, a check row in view, periods running the same way on every sheet - and give the agent an actual picture of that layout, not just the principle.

The gap this closes: the agent knows the rules conceptually ("segregate inputs", "one schedule per area") but on a blank canvas produces something technically fine yet spatially incoherent - assumptions scattered, no consistent home for the output, periods running in odd directions. A picture fixes that. The discipline is to anchor to a known-good layout pattern and adapt it to the model in hand - never to trace it rigidly, and never to invent a fresh structure each time.

### What Good Looks Like
A reviewer opening the model can navigate it without a guide:
1. Inputs are obvious and in one place - a dedicated assumptions block/sheet, not buried inside formulas.
2. The build flows to a clear output - top-to-bottom to a delineated total, left-to-right across periods.
3. A check row is visible - the balance/tie reads near the output, not hidden.
4. Every sheet uses the same period columns - column F is the same year on every tab.
5. The workbook is organised - tabs in a logical order, key anchors named, nothing orphaned.

### The Visual Anchor - Tab and Workbook Anatomy
This is the heart of the skill: a picture to build to. Treat both as patterns to adapt, not grids to trace - the principles hold; the exact rows, periods and schedule count vary with the model.

**Anatomy of a good tab:**
- Column A is a narrow spacer (nothing starts in A1).
- Labels, section headers and row descriptions start in column B.
- Values, inputs, scenarios and time periods start in column C or later.
- Title block states what the tab is, its units and currency, once.
- Assumptions sit on top, segregated, because they're what you change.
- Periods run left-to-right, actuals then forecast, labelled A/E, and the same columns mean the same years on every sheet.
- The build flows top-to-bottom to a delineated output/total.
- A check row sits in view near the output.
- The source line anchors the base.
- Whitespace separates blocks - one logical block per area.

**A good workbook (default tab order - adapt for model size):**
Cover > Control / Assumptions > Drivers > Schedules > Statements > Output / Summary > Sensitivities

For a quick model, collapse: a single tab can hold assumptions -> build -> output; a one-page comps needs no statements tab. For a full integrated build, each gets its own tab. The order is the pattern - adapt the number of tabs to the model, but keep the flow (inputs -> engine -> output -> sensitivity) and keep period columns aligned across all of them.

**Adapt, don't trace.** More or fewer drivers, segments, schedules or years; output on its own tab or beneath the build - all fine. What must hold: inputs segregated and on top, periods left-to-right and aligned across sheets, a visible output and check, a source line, and a navigable tab flow.

### Conventions

**Input segregation.** Inputs live in a dedicated block or control sheet, never scattered inside calculation areas. This skill places them; Data & Formula skills classifies them (and Model Formatting skills colours them) - together they make inputs unmistakable.

**Period alignment.** The same column is the same period on every sheet, so a link across tabs always lines up. Misaligned period columns are a top source of cross-sheet errors.

**One block per area, with whitespace.** Each schedule or statement is its own block; blank rows/columns separate them; nothing is crammed.

**Column widths and wrapping.** Label, period and numeric columns are fitted to their content so the grid stays compact. A column carrying narrative text - commentary, descriptions, notes, source basis - is wrapped and given a deliberate readable width (roughly 20-42 Excel width units), because auto-fit leaves an already-wrapped column at whatever width it had and grows the row height instead. A text-heavy column must never be left at a numeric column's width.

**Output and drivers in view.** The headline output and the one or two drivers that move it sit on the same screen (the "key drivers visible" principle the output skills rely on).

**Generic template mechanics.** When populating a bank template: map data in by economic meaning, not row position or label; adapt the structure to the data (add/remove rows to fit) rather than force-fitting; remove unused placeholders; preserve the template's layout, named ranges and articulation. (The output skills' "Working with a template" sections defer these generic mechanics here.)

**Reading an existing/unfamiliar model.** Before editing one you didn't build, map its structure first - where the assumptions live, where the output is, how the tabs flow, what feeds what - by economic meaning, not by label or position. Editing before mapping is how an unfamiliar model gets broken.

### Failure Modes
| Symptom | Likely Cause | Fix |
|---|---|---|
| Blank-canvas model is spatially incoherent | No layout pattern applied | Build to the tab anatomy; adapt, don't invent fresh |
| Inputs buried inside formula sheets | Not segregated | A dedicated assumptions block / control sheet |
| A cross-sheet link lands on the wrong year | Period columns differ across tabs | Same period columns on every sheet |
| Can't find the output, or no check row | Output/check not placed | A delineated output total and a visible check row |
| A text column renders one word per line | Wrapped column left to auto-fit | Wrap it and set a readable explicit width |
| Editing an unfamiliar model breaks it | Structure not mapped first | Read the structure by meaning before editing |
| Template placeholders left blank or filled with junk | Mechanics not applied | Adapt rows to data; remove unused placeholders |

### Validation Rules

**Hard fails (block output):**
- Period alignment: the same column is the same period on every sheet (no tab offset).
- Input segregation: no orphan input cell buried inside a calculation block.

**Warnings (flag, do not block):**
- No dedicated assumptions block / control sheet.
- An output sheet without a visible output total or check row.
- Cross-sheet anchors not named where named ranges would make links robust.
- Unused template placeholders left in the model.`;
